/** Central KYC — constants and normalized document taxonomy */

export const KYC_STATUS = {
  DRAFT: "draft",
  IN_REVIEW: "in_review",
  APPROVED: "approved",
  EXPIRED: "expired",
  MISSING_DOCS: "missing_docs",
  REJECTED: "rejected",
};

export const KYC_RISK = {
  LOW: "low",
  MEDIUM: "medium",
  HIGH: "high",
};

export const ACCOUNT_STATE = {
  ACTIVE: "active",
  CLOSED: "closed",
  ZERO: "zero",
};

/** users.status in Firestore (Customer Management table) */
export const APP_USER_STATUS = {
  PENDING: 0,
  APPROVED: 1,
  REJECTED: 2,
  BLOCKED: 3,
};

export function appUserStatusLabel(status) {
  const s = Number(status);
  if (s === APP_USER_STATUS.APPROVED) return "Approved";
  if (s === APP_USER_STATUS.REJECTED) return "Rejected";
  if (s === APP_USER_STATUS.BLOCKED) return "Blocked";
  return "Pending";
}

export function appUserStatusColor(status) {
  const s = Number(status);
  if (s === APP_USER_STATUS.APPROVED) return "success";
  if (s === APP_USER_STATUS.REJECTED) return "error";
  if (s === APP_USER_STATUS.BLOCKED) return "error";
  return "warning";
}

export const CLIENT_CLASSIFICATION = {
  INDIVIDUAL: "individual",
  CORPORATE: "corporate",
};

export const KYC_DOC_STATUS = {
  VALID: "valid",
  MISSING: "missing",
  EXPIRED: "expired",
  NEEDS_REVIEW: "needs_review",
};

export const SCREENING_STATUS = {
  PENDING: "pending",
  CLEAR: "clear",
  HIT: "hit",
  MANUAL_REVIEW: "manual_review",
};

/** Normalized document types (regulatory taxonomy) */
export const KYC_DOC_TYPES = {
  PASSPORT: "passport",
  NATIONAL_ID: "national_id",
  DRIVERS_LICENSE: "drivers_license",
  PROOF_OF_ADDRESS: "proof_of_address",
  CORPORATE_REGISTRATION: "corporate_registration",
  CORPORATE_ARTICLES: "corporate_articles",
  CONTRACT: "contract",
  CONTRACT_AMENDMENT: "contract_amendment",
  WALLET_AMENDMENT: "wallet_amendment",
  TAX: "tax",
  OTHER: "other",
};

/** Maps users.document_type — UI AddCustomer: id 1=Passport, 2=ID-Card; CF comment: 0=Passport, 1=ID */
export const LEGACY_DOCUMENT_TYPE_MAP = {
  0: KYC_DOC_TYPES.PASSPORT,
  1: KYC_DOC_TYPES.PASSPORT,
  2: KYC_DOC_TYPES.NATIONAL_ID,
};

export const REQUIRED_DOCS_INDIVIDUAL = [
  KYC_DOC_TYPES.PASSPORT,
  KYC_DOC_TYPES.NATIONAL_ID,
  KYC_DOC_TYPES.DRIVERS_LICENSE,
];

export const REQUIRED_DOCS_CORPORATE = [
  KYC_DOC_TYPES.CORPORATE_REGISTRATION,
  KYC_DOC_TYPES.CORPORATE_ARTICLES,
];

export const WALLET_RECON_STATUS = {
  MATCH: "match",
  MISMATCH: "mismatch",
  MISSING_REGISTERED: "missing_registered",
  MISSING_CONTRACT: "missing_contract",
  NEVER_WITHDRAWN: "never_withdrawn",
  NEEDS_REVIEW: "needs_review",
};

export const CONFIDENCE = {
  HIGH: "high",
  MEDIUM: "medium",
  LOW: "low",
  MANUAL: "manual",
};

/** In-app identification document update (Home banner). Stored on users + kyc_profiles. */
export const DOCUMENT_UPDATE_STATUS = {
  PENDING: "pending",
  SUBMITTED: "submitted",
  APPROVED: "approved",
  REJECTED: "rejected",
};

export const DOCUMENT_UPDATE_REASONS = [
  { id: "expired", label: "Expired" },
  { id: "poor_photo", label: "Poor photo (glare, cropped, unreadable)" },
  { id: "wrong_type", label: "Wrong type / missing document" },
];

export function documentUpdateStatusLabel(status) {
  if (status === DOCUMENT_UPDATE_STATUS.PENDING) return "Waiting for client";
  if (status === DOCUMENT_UPDATE_STATUS.SUBMITTED) return "Review";
  if (status === DOCUMENT_UPDATE_STATUS.APPROVED) return "Approved";
  if (status === DOCUMENT_UPDATE_STATUS.REJECTED) return "Resubmit";
  return "";
}

/** Infer document type from filename/path */
export function inferDocTypeFromPath(filePath = "") {
  const p = String(filePath).toLowerCase();
  if (p.includes("passport") || p.includes("passaporte")) return KYC_DOC_TYPES.PASSPORT;
  if (p.includes("cnh") || p.includes("drivers") || p.includes("license") || p.includes("licence"))
    return KYC_DOC_TYPES.DRIVERS_LICENSE;
  if (p.includes("carta_ident") || p.includes("id-card") || p.includes("id_card") || p.includes("identita"))
    return KYC_DOC_TYPES.NATIONAL_ID;
  if (p.includes("proof_of_address") || p.includes("residenza") || p.includes("domicile") || p.includes("address"))
    return KYC_DOC_TYPES.PROOF_OF_ADDRESS;
  if (p.includes("cnpj")) return KYC_DOC_TYPES.CORPORATE_REGISTRATION;
  if (p.includes("contrato social") || p.includes("contrato_social")) return KYC_DOC_TYPES.CORPORATE_ARTICLES;
  if (p.includes("wallet") && (p.includes("amendment") || p.includes("change") || p.includes("deposit")))
    return KYC_DOC_TYPES.WALLET_AMENDMENT;
  if (p.includes("amendment") || p.includes("beneficiary")) return KYC_DOC_TYPES.CONTRACT_AMENDMENT;
  if (p.includes("intermediation") || p.includes("contract") || p.includes("contrato"))
    return KYC_DOC_TYPES.CONTRACT;
  if (p.includes("tax") || p.includes("kraken")) return KYC_DOC_TYPES.TAX;
  return KYC_DOC_TYPES.OTHER;
}

export function normalizeNameKey(name = "") {
  return String(name)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
}

export function inferAccountStateFromFolder(folderPath = "") {
  const p = String(folderPath).toLowerCase();
  if (p.includes("__closed_accounts")) return ACCOUNT_STATE.CLOSED;
  if (p.includes("xx__old_zero") || p.includes("z_")) return ACCOUNT_STATE.ZERO;
  return ACCOUNT_STATE.ACTIVE;
}

export function inferClassificationFromFiles(files = []) {
  const joined = files.join(" ").toLowerCase();
  if (joined.includes("cnpj") || joined.includes("contrato social") || joined.includes("corporate"))
    return CLIENT_CLASSIFICATION.CORPORATE;
  return CLIENT_CLASSIFICATION.INDIVIDUAL;
}
